// src/components/admin/StaffSalesPanel.js
// ============================================================================
// « MES VENTES » — encart du tableau de bord pour un membre du personnel.
// Livré par AbodyStudio Limited — https://abodystudio.com
//
// Affiche le perimetre reel du compte connecte : ses marchands, leurs ventes
// encaissees, et sa file d'annonces a traiter.
//
// Le montant n'est pas seulement masque ici quand le droit manque : le serveur
// renvoie `revenue: null`. Masquer un chiffre cote navigateur ne le protege
// pas — il se lit dans l'onglet reseau.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiTrendingUp, FiShoppingBag, FiClock, FiBriefcase, FiLoader, FiCalendar, FiRefreshCw } from 'react-icons/fi';
import API from '../../services/api';

const PERIODS = [
  { v: 'month', l: 'Ce mois-ci' }, // v1.400.0 — du 1er du mois a aujourd'hui
  { v: 7, l: '7 jours' },
  { v: 30, l: '30 jours' },
  { v: 90, l: '3 mois' },
];

const StaffSalesPanel = () => {
  const [days, setDays] = useState('month');
  // v1.402.0 — 'me' = MES ventes (creditees a moi) ; 'all' = plateforme (admin)
  const [scope, setScope] = useState('me');
  // Intervalle personnalise. Vide = on reste sur les boutons 7 / 30 / 90 jours.
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showDaily, setShowDaily] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (d, f, t, sc) => {
    setLoading(true);
    try {
      // Un intervalle complet remplace le nombre de jours. On n'envoie `from`
      // et `to` que si les DEUX sont saisis : une seule borne donnerait une
      // periode ouverte, que l'utilisateur n'a pas demandee.
      const params = (f && t) ? { from: f, to: t } : (d === 'month' ? { period: 'month' } : { days: d });
      if (sc === 'all') params.scope = 'all';
      const res = await API.get('/admin/staff/me/sales', { params });
      setData(res?.data?.data || null);
      setFailed(false);
    } catch (e) {
      // Silencieux : cet encart est un complement du tableau de bord. Une erreur
      // ici ne doit pas couvrir l'ecran d'un message rouge.
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days, from, to, scope); }, [days, from, to, scope, load]);

  // v1.407.0 — les chiffres ne restent plus figes a l'heure d'ouverture de la
  // page : rechargement toutes les 2 minutes et au retour sur l'onglet.
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') load(days, from, to, scope); };
    const timer = setInterval(refresh, 120000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [days, from, to, scope, load]);

  if (failed) return null;

  if (loading && !data) {
    return (
      <div className="bg-white rounded-lg shadow p-6 flex items-center gap-2 text-gray-400 text-sm">
        <FiLoader className="animate-spin" size={15} /> Chargement de vos ventes…
      </div>
    );
  }
  if (!data) return null;

  const jourFr = (k) => (k ? String(k).slice(0, 10).split('-').reverse().join('/') : '—');
  const heureFr = (iso) => (iso
    ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Casablanca' })
    : new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));

  const money = (v) =>
    v === null || v === undefined ? '—' : `${Number(v).toLocaleString('fr-FR')} MAD`;

  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <FiTrendingUp className="text-[#e11d7b]" size={18} /> Mes ventes
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {data.scope === 'all'
              ? 'Toutes les ventes de la plateforme'
              : 'Vos ventes : commandes saisies par vous ou créditées à votre nom'}
          </p>
          {data.canSeeAll && (
            <div className="inline-flex mt-2 rounded-lg border overflow-hidden text-xs font-bold">
              <button type="button" onClick={() => setScope('me')}
                className={`px-3 py-1 ${scope === 'me' ? 'bg-[#e11d7b] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                Mes ventes
              </button>
              <button type="button" onClick={() => setScope('all')}
                className={`px-3 py-1 border-l ${scope === 'all' ? 'bg-[#e11d7b] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                Toute la plateforme
              </button>
            </div>
          )}
        </div>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.v}
              type="button"
              onClick={() => { setFrom(''); setTo(''); setDays(p.v); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${
                (!from || !to) && days === p.v ? 'bg-[#e11d7b] text-white border-[#e11d7b]' : 'hover:bg-gray-50'
              }`}
            >
              {p.l}
            </button>
          ))}
        </div>
      </div>

      {/* INTERVALLE PERSONNALISE.
          Les dates ne sont envoyees que lorsque les DEUX sont saisies : une
          seule borne donnerait une periode ouverte, que personne n'a demandee.
          Choisir un intervalle desactive visuellement les boutons 7/30/90 —
          sans quoi on ne saurait pas quelle periode est reellement affichee. */}
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-[11px] text-gray-500 mb-0.5">Du</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="border rounded-lg px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] text-gray-500 mb-0.5">Au</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="border rounded-lg px-2 py-1.5 text-sm" />
        </div>
        {from && to && (
          <button type="button" onClick={() => { setFrom(''); setTo(''); }}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold border hover:bg-gray-50">
            Effacer
          </button>
        )}
        {Array.isArray(data?.daily) && data.daily.length > 0 && (
          <button type="button" onClick={() => setShowDaily((v) => !v)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold border hover:bg-gray-50 ml-auto">
            {showDaily ? 'Masquer le détail' : 'Voir jour par jour'}
          </button>
        )}
      </div>

      {showDaily && Array.isArray(data?.daily) && (
        <div className="mb-5 border rounded-lg overflow-hidden">
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 sticky top-0">
                <tr>
                  <th className="py-2 px-3 text-left">Jour</th>
                  <th className="py-2 px-3 text-right">Ventes</th>
                  <th className="py-2 px-3 text-right">Chiffre d'affaires</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {/* Les journees SANS vente sont affichees a 0 : un tableau qui
                    saute du 3 au 7 laisse croire a un bug, alors qu'il s'agit
                    de jours sans activite — une information en soi. */}
                {data.daily.map((d) => (
                  <tr key={d.date} className={d.orders === 0 ? 'text-gray-400' : ''}>
                    <td className="py-1.5 px-3">{d.date.split('-').reverse().join('/')}</td>
                    <td className="py-1.5 px-3 text-right">{d.orders}</td>
                    <td className="py-1.5 px-3 text-right font-semibold">{money(d.revenue)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-bold">
                <tr>
                  <td className="py-2 px-3">Total du {jourFr(data.from)} au {jourFr(data.to)}</td>
                  <td className="py-2 px-3 text-right">{data.ordersCount}</td>
                  <td className="py-2 px-3 text-right text-[#e11d7b]">{money(data.revenue)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* v1.407.0 — PERIODE REELLEMENT AFFICHEE + heure du calcul. Sans elle,
          « 58 ventes » ne disait pas depuis quand ni jusqu'a quand. */}
      <div className="mb-3 rounded-lg border border-pink-100 bg-pink-50 px-3 py-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-gray-800 flex items-center gap-2">
          <FiCalendar className="text-[#e11d7b]" size={14} />
          <span>
            Période : du <strong>{jourFr(data.from)}</strong> au <strong>{jourFr(data.to)}</strong>
            {!(from && to) && data.to && ' (aujourd\'hui inclus)'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>Mis à jour à {heureFr(data.generatedAt)}</span>
          <button type="button" onClick={() => load(days, from, to, scope)} disabled={loading}
            className="px-2 py-1 rounded-md border bg-white font-semibold text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-60">
            <FiRefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="border rounded-lg p-3">
          <div className="text-xs text-gray-500 flex items-center gap-1.5">
            <FiShoppingBag size={13} /> Ventes encaissées
          </div>
          <div className="text-[11px] text-gray-400">du {jourFr(data.from)} au {jourFr(data.to)}</div>
          <div className="text-2xl font-bold mt-1">{data.ordersCount}</div>
        </div>

        {/* Encart CA masque si le droit `stats.amounts` n'est pas accorde. */}
        {data.revenue !== null && (
          <div className="border rounded-lg p-3">
            <div className="text-xs text-gray-500 flex items-center gap-1.5">
              <FiTrendingUp size={13} /> Chiffre d'affaires
            </div>
            <div className="text-[11px] text-gray-400">du {jourFr(data.from)} au {jourFr(data.to)}</div>
            <div className="text-2xl font-bold mt-1 text-[#e11d7b]">{money(data.revenue)}</div>
          </div>
        )}

        {/* MARGE REELLE. Le chiffre d'affaires seul ne dit rien de ce que la
            vente rapporte : sur une offre a 500 avec un prix partenaire a 200,
            Maxula gagne 300, pas 500. Zero avec des ventes n'est pas une
            anomalie — les gains ne sont libérés qu'à la clôture. */}
        {data.margin !== null && data.margin !== undefined && (
          <div className="border rounded-lg p-3 bg-green-50 border-green-200">
            <div className="text-xs text-green-800 flex items-center gap-1.5">
              <FiTrendingUp size={13} /> Marge Maxula
            </div>
            <div className="text-2xl font-bold mt-1 text-green-700">{money(data.margin)}</div>
            <div className="text-[11px] text-green-800/70 mt-0.5">
              Vente moins ce qui revient aux partenaires — comptée dès le paiement
            </div>
            {/* v1.408.0 — la marge est comptee a la VENTE ; la validation du
                coupon ne sert qu'a payer le partenaire. On montre quelle part
                attend encore cette validation. */}
            {Number(data.marginPending) > 0 && (
              <div className="text-[11px] text-green-900 mt-1">
                dont {money(data.marginPending)} sur coupons pas encore utilisés
              </div>
            )}
          </div>
        )}

        <Link
          to="/admin/deals?status=pending"
          className="border rounded-lg p-3 hover:bg-gray-50 transition"
        >
          <div className="text-xs text-gray-500 flex items-center gap-1.5">
            <FiClock size={13} /> Annonces à valider
          </div>
          <div className={`text-2xl font-bold mt-1 ${data.pendingDeals ? 'text-amber-600' : ''}`}>
            {data.pendingDeals}
          </div>
        </Link>
      </div>

      <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
        <FiBriefcase size={13} /> Par marchand
      </div>
      {data.merchants.length === 0 ? (
        <p className="text-sm text-gray-400">
          Aucune vente encaissée sur la période.
        </p>
      ) : (
        <div className="border rounded-lg divide-y">
          {data.merchants.map((m) => (
            <div key={m.merchantId} className="flex items-center justify-between px-3 py-2">
              <span className="text-sm text-gray-800 truncate">{m.name}</span>
              <span className="text-sm text-gray-600 shrink-0">
                {m.orders} vente{m.orders > 1 ? 's' : ''}
                {m.revenue !== null ? ` · ${money(m.revenue)}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StaffSalesPanel;
